import { FluentBundle } from 'fluent';
import {
  GetString,
} from 'fluent-react';
import React from 'react';
import styled from 'styled-components';
import { LookBackYearRange } from '../../graphQL/types/shared';
import {
  TradeFlow,
} from '../../graphQL/types/shared';
import PresentationManager from '../../presentationManager/PresentationManager';
import { latestYear } from '../../Utils';
import { VizType } from '../../viz/Utils';
import CountrySelector from '../countrySelector/InteriorCountrySelector';
import ExploreEngageButtons from '../exploreEngageButtons';
import {
  getTradeFlowOptions,
  TradeFlowSelectorContainer,
} from '../exportBasket';
import LeftPanelBoxShadow from '../exportBasket/LeftPanelBoxShadow';
import Legend from '../exportComplexityLegend';
import GraphControlDropdown, {
  width as graphControlDropdownWidth,
} from '../graphControlDropdown';
import {
  IOption,
} from '../graphControlDropdown';
import {
  GraphControlsContainer,
} from '../Grid';
import {
  Root,
} from '../Grid';
import HamburgerMenu from '../menu';
import SectionNav from '../sectionNav';
import Sidebar from '../sidebar';
import TreeMap from '../treeMap';
import UtilityBar from '../utilityBarItems';
import {chapters} from '../utilityBarItems/VideoTutorial';
import {
  CommonElementName,
  defaultFadeTransition,
  defaultVerticalSlideTransition,
  getUniqueSlideElementKey,
  SlideName,
  TreeMapColorCriterion,
} from '../Utils';
import TopOfPageIndicators from './TopOfPageIndicators';
import {
  separationBetweenControlDropdowns,
} from './Utils';

//#region Styling
export const ComplexitySelectorContainer = styled(GraphControlsContainer)`
  margin-left: ${graphControlDropdownWidth + separationBetweenControlDropdowns}rem;
  width: ${graphControlDropdownWidth}rem;
`;
//#endregion

const getTreMapColorCriterionOptions =
  (getFluentString: GetString): Array<IOption<TreeMapColorCriterion>> => ([
    {
      value: TreeMapColorCriterion.Complexity,
      label: getFluentString('tree-map-color-criterion-selector-complexity'),
      isEnabled: true,
    },
    {
      value: TreeMapColorCriterion.EntryYear,
      label: getFluentString('tree-map-color-criterion-selector-entry-year'),
      isEnabled: false,
    },
  ]);

interface IInput {
  getFluentString: GetString;
  countryId: string;
  year: number;
  selectCountry: (country: string) => void;
  slideIndex: number;
  setSlideIndex: (index: number) => void;
  firstContentfulSlideIndex: number;
  tradeFlow: TradeFlow;
  setTradeFlow: (value: TradeFlow) => void;
  colorCriterion: TreeMapColorCriterion;
  setColorCriterion: (value: TreeMapColorCriterion) => void;
  goToNextSlide: () => void;
  goToPrevSlide: () => void;
  fluentBundle: FluentBundle;
  tradeFlowDropdownZIndex: number;
  colorCriterionDropdownZIndex: number;
  topOfPageIndicatorsZIndex: number;
  exportBasketExportComplexityTreeMapZIndex: number;
  legendZIndex: number;
  sidebarZIndex: number;
  hamburgerMenuZIndex: number;
  countrySelectorZIndex: number;
  sectionNavZIndex: number;
  exploreEngageButtonsZIndex: number;
  helpTipsTriggerZIndex: number;
  toggleHelpTooltips: () => void;
  helpTooltipsActive: boolean;
  promotedZIndex: number;
}
export default (input: IInput) => {
  const {
    countryId, year, selectCountry, slideIndex, setSlideIndex,
    firstContentfulSlideIndex, tradeFlow, setTradeFlow,
    colorCriterion, setColorCriterion,
    tradeFlowDropdownZIndex, colorCriterionDropdownZIndex,
    topOfPageIndicatorsZIndex, legendZIndex,
    goToNextSlide, goToPrevSlide, fluentBundle, hamburgerMenuZIndex, sidebarZIndex,
    exportBasketExportComplexityTreeMapZIndex, countrySelectorZIndex,
    sectionNavZIndex, getFluentString, exploreEngageButtonsZIndex, helpTipsTriggerZIndex,
    toggleHelpTooltips, helpTooltipsActive, promotedZIndex,
  } = input;

  const helpTooltipOrder = {
    dropdown: 1,
    sectionNav: 2,
    pageAdvancer: 3,
    exploreButton: 4,
  };

  return (
    <PresentationManager RootComponent={Root} slideIndex={slideIndex}>
      <CountrySelector
        selectedCountry={countryId}
        onCountrySelect={selectCountry}
        promptText={getFluentString('country-selector-prompt')}
        getFluentString={getFluentString}
        zIndex={countrySelectorZIndex}
        transition={defaultVerticalSlideTransition}
        key={CommonElementName.CountrySelector}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.dropdown}
      />
      <SectionNav
        startingIndexOfFirstSection={0}
        slideIndex={slideIndex}
        setSlideIndex={setSlideIndex}
        year={year}
        getFluentString={getFluentString}
        countryId={countryId}
        zIndex={sectionNavZIndex}
        key={CommonElementName.SectionNav}
        transition={defaultVerticalSlideTransition}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.sectionNav}
      />
      <Sidebar
        firstContentfulSlideIndex={firstContentfulSlideIndex}
        slideIndex={slideIndex}
        getFluentString={getFluentString}
        goToNextSlide={goToNextSlide}
        goToPrevSlide={goToPrevSlide}
        countryId={countryId}
        fluentBundle={fluentBundle}
        yearRange={LookBackYearRange.FifteenYears}
        zIndex={sidebarZIndex}
        transition={defaultVerticalSlideTransition}
        key={CommonElementName.Sidebar}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.pageAdvancer}
      />
      <LeftPanelBoxShadow
        transition={defaultVerticalSlideTransition}
        key={CommonElementName.LeftPanelBackground}
      />
      <TopOfPageIndicators
        countryId={countryId}
        getFluentString={getFluentString}
        zIndex={topOfPageIndicatorsZIndex}
        transition={defaultFadeTransition}
        key={getUniqueSlideElementKey(SlideName.ExportComplexity, 'top-page-indicators')}
      />
      <HamburgerMenu
        getFluentString={getFluentString}
        zIndex={hamburgerMenuZIndex}
        transition={defaultFadeTransition}
        key={CommonElementName.HamburgerMenu}
      />
      <UtilityBar
        getFluentString={getFluentString}
        zIndex={helpTipsTriggerZIndex}
        toggleHelpTooltips={toggleHelpTooltips}
        helpTooltipsActive={helpTooltipsActive}
        transition={defaultFadeTransition}
        countryId={countryId}
        videoChapter={chapters[3]}
        key={CommonElementName.HelpTipsTrigger}
      />
      <GraphControlDropdown
        label={getFluentString('trade-flow-graph-control-label')}
        selectedValue={tradeFlow}
        options={getTradeFlowOptions(getFluentString)}
        onValueSelect={setTradeFlow}
        zIndex={tradeFlowDropdownZIndex}
        Container={TradeFlowSelectorContainer}
        promotedZIndex={promotedZIndex}
        transition={defaultFadeTransition}
        key={CommonElementName.ExportBasketExportComplexityTradeFlowSelector}
      />
      <GraphControlDropdown
        label={getFluentString('tree-map-color-criterion-selector-label')}
        selectedValue={colorCriterion}
        options={getTreMapColorCriterionOptions(getFluentString)}
        onValueSelect={setColorCriterion}
        zIndex={colorCriterionDropdownZIndex}
        Container={ComplexitySelectorContainer}
        promotedZIndex={promotedZIndex}
        transition={defaultFadeTransition}
        key={CommonElementName.ExportBasketExportComplexityColorSelector}
      />
      <ExploreEngageButtons
        zIndex={exploreEngageButtonsZIndex}
        year={year}
        countryId={countryId}
        vizType={VizType.Tree}
        getFluentString={getFluentString}
        transition={defaultFadeTransition}
        key={CommonElementName.ExploreEngageButtons}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.exploreButton}
      />
      <TreeMap
        tradeFlow={tradeFlow}
        countryId={countryId}
        colorCriterion={TreeMapColorCriterion.Complexity}
        getFluentString={getFluentString}
        year={latestYear}
        onlyShowNewProducts={false}
        zIndex={exportBasketExportComplexityTreeMapZIndex}
        transition={defaultFadeTransition}
        key={CommonElementName.ExportBasketExportComplexityTreeMap}
      />
      <Legend
        countryId={countryId}
        year={latestYear}
        getFluentString={getFluentString}
        zIndex={legendZIndex}
        showEci={true}
        transition={defaultFadeTransition}
        key={getUniqueSlideElementKey(SlideName.ExportComplexity, 'graph-legend')}
      />
    </PresentationManager>
  );
};
